export const API_ENDPOINTS = {
  // Auth
  AUTH: {
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
    VERIFY_EMAIL: '/auth/verify-email',
    VERIFY_OTP: '/auth/verify-otp',
    VERIFY_RESET_OTP: '/auth/verify-reset-otp',
    RESEND_OTP: '/auth/resend-otp',
    ME: '/auth/me',
    // Google OAuth
    GOOGLE: '/auth/google',
    GOOGLE_LINK: '/auth/google/link',
    GOOGLE_UNLINK: '/auth/google/unlink',
  },

  // Users
  USERS: {
    BASE: '/users',
    PROFILE: '/users/profile',
    UPDATE_PROFILE: '/users/profile',
    ME: '/users/me',
  },

  // Wallet
  WALLET: {
    BASE: '/wallet',
    TRANSACTIONS: '/wallet/transactions',
    EARN_CREDIT: '/wallet/earn-credit',
    REPORT: '/wallet/report',
  },

  // Ads
  ADS: {
    BASE: '/ads',
    SEARCH: '/ads/search',
    BY_CATEGORY: (categoryId: string) => `/ads/category/${categoryId}`,
    MY_ADS: '/ads/my-ads',
    DETAIL: (id: string) => `/ads/${id}`,
    BY_SLUG: (slug: string) => `/ads/slug/${slug}`,
    CLOSE: (id: string) => `/ads/${id}/close`,
    UNLOCK_CONTACT: (id: string) => `/ads/${id}/unlock-contact`,
    CHECK_CONTACT_UNLOCK: (id: string) => `/ads/${id}/contact-unlocked`,
    REOPEN: (id: string) => `/ads/${id}/reopen`,
    TOGGLE_BIDS_VISIBILITY: (id: string) => `/ads/${id}/toggle-bids-visibility`,
  },

  // Bids
  BIDS: {
    BASE: '/bids',
    BY_AD: (adId: string) => `/bids/ad/${adId}`,
    CREATE: (adId: string) => `/bids/ad/${adId}`,
    MY_BIDS: '/bids/my-bids',
    DETAIL: (id: string) => `/bids/${id}`,
    ACCEPT: (id: string) => `/bids/${id}/accept`,
    REJECT: (id: string) => `/bids/${id}/reject`,
    WITHDRAW: (id: string) => `/bids/${id}/withdraw`,
  },

  // Comments
  COMMENTS: {
    BY_AD: (adId: string) => `/comments/ad/${adId}`,
    CREATE: (adId: string) => `/comments/ad/${adId}`,
    DETAIL: (id: string) => `/comments/${id}`,
    REACT: (id: string) => `/comments/${id}/react`,
  },

  // Chat message reactions
  CHAT_MESSAGE_REACT: (messageId: string) => `/chat/messages/${messageId}/react`,

  // Categories
  CATEGORIES: {
    BASE: '/categories',
    TREE: '/categories/tree',
    DETAIL: (id: string) => `/categories/${id}`,
    BY_SLUG: (slug: string) => `/categories/slug/${slug}`,
    BREADCRUMB: (id: string) => `/categories/${id}/breadcrumb`,
  },

  // Chat
  CHAT: {
    CONVERSATIONS: '/chat/conversations',
    CONVERSATION_DETAIL: (id: string) => `/chat/conversations/${id}`,
    START: '/chat/conversations/start',
    MESSAGES: (conversationId: string) => `/chat/conversations/${conversationId}/messages`,
    MESSAGE_DETAIL: (conversationId: string, messageId: string) => `/chat/conversations/${conversationId}/messages/${messageId}`,
    MARK_READ: (conversationId: string) => `/chat/conversations/${conversationId}/read`,
    UNREAD_COUNT: '/chat/unread-count',
  },

  // Upload
  UPLOAD: {
    IMAGE: '/upload/image',
    IMAGES: '/upload/images',
    PRESIGNED_URL: '/upload/presigned-url',
    PRESIGNED_URLS: '/upload/presigned-urls',
  },

  // Newsletter
  NEWSLETTER: {
    SUBSCRIBE: '/newsletter/subscribe',
    UNSUBSCRIBE: '/newsletter/unsubscribe',
  },

  // Cities (public)
  CITIES: {
    BASE: '/cities',
  },

  // Settings (public)
  SETTINGS: {
    CREDITS: '/settings/credits',
    CREDIT_PACKAGES: '/settings/credit-packages',
    SOCIAL_LINKS: '/settings/social-links',
    SUPPORT_SEND: '/settings/support/send',
    LEGAL_PAGE: (slug: string) => `/settings/legal/${slug}`,
  },

  // Admin endpoints
  ADMIN: {
    LOGIN: '/admin/login',
    LOGOUT: '/admin/logout',
    ME: '/admin/me',
    FORGOT_PASSWORD: '/admin/forgot-password',
    RESET_PASSWORD: '/admin/reset-password',
    REFRESH: '/admin/refresh',
    DASHBOARD: '/admin/dashboard',

    // User management
    USERS: '/admin/users',
    CREATE_USER: '/admin/users',
    USER_DETAIL: (id: string) => `/admin/users/${id}`,
    ADJUST_CREDITS: (id: string) => `/admin/users/${id}/adjust-credits`,
    UPDATE_STATUS: (id: string) => `/admin/users/${id}/status`,

    // Ad management
    ADS: '/admin/ads',
    AD_DETAIL: (id: string) => `/admin/ads/${id}`,
    AD_RENEW: (id: string) => `/admin/ads/${id}/renew`,

    // Category management
    CATEGORIES: '/admin/categories',
    CATEGORY_DETAIL: (id: string) => `/admin/categories/${id}`,
    CATEGORY_SNAPSHOTS: '/admin/categories/snapshots',
    CATEGORY_SNAPSHOT_DETAIL: (id: string) => `/admin/categories/snapshots/${id}`,
    CATEGORY_SNAPSHOT_RESTORE: (id: string) => `/admin/categories/snapshots/${id}/restore`,

    // Transaction management
    TRANSACTIONS: '/admin/transactions',

    // Analytics
    ANALYTICS: '/admin/analytics',

    // Newsletter management
    NEWSLETTER: {
      SUBSCRIBERS: '/admin/newsletter/subscribers',
      CAMPAIGNS: '/admin/newsletter/campaigns',
      CAMPAIGN_DETAIL: (id: string) => `/admin/newsletter/campaigns/${id}`,
      SEND: (id: string) => `/admin/newsletter/campaigns/${id}/send`,
    },

    // Settings management
    SETTINGS_CREDITS: '/admin/settings/credits',
    SETTINGS_CREDIT_PACKAGES: '/admin/settings/credit-packages',
    SETTINGS_SOCIAL_LINKS: '/admin/settings/social-links',
    SETTINGS_BANNED_WORDS: '/admin/settings/banned-words',
    SETTINGS_UPLOAD_BANNER: '/admin/settings/upload-banner',
    SETTINGS_LEGAL: '/admin/settings/legal',
    SETTINGS_LEGAL_PAGE: (slug: string) => `/admin/settings/legal/${slug}`,

    // City management
    CITIES: '/admin/cities',
    CITY_DETAIL: (id: string) => `/admin/cities/${id}`,
    CITIES_REORDER: '/admin/cities/reorder',
    CITIES_EXCEL_TEMPLATE: '/admin/cities/excel/template',
    CITIES_EXCEL_EXPORT: '/admin/cities/excel/export',
    CITIES_EXCEL_IMPORT: '/admin/cities/excel/import',

    // Profile (self)
    UPDATE_PROFILE: '/admin/profile',
    UPLOAD_AVATAR: '/admin/profile/avatar',
    CHANGE_PASSWORD: '/admin/profile/change-password',

    // Admin management (SUPER_ADMIN only)
    ADMINS: '/admin/admins',
    ADMIN_DETAIL: (id: string) => `/admin/admins/${id}`,
  },
} as const;

export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  UNPROCESSABLE_ENTITY: 422,
  INSUFFICIENT_CREDITS: 402, // Payment Required - used for credit issues
  INTERNAL_SERVER_ERROR: 500,
  SERVICE_UNAVAILABLE: 503,
} as const;
